// ABOUTME: Step 1 of license purchase (DVLP-7860): the offers as columns, so every option
// ABOUTME: fits one view and position is a deliberate lever rather than an accident of sort order.
import { just, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import { Button, Typography, emptyFragment } from '@execonline-inc/execonline-ui';
import { NotTranslated, T } from '../../../../../Translations';
import { LicenseOffer } from '../../LicenseOffers/Types';
import licensePurchaseStore from '../Store';
import { Choosing, Entry } from '../Types';
import { Pane, ShelfPrice, TermLabel, WizardSteps } from './Common';

const columnsFor = (count: number): string => {
  if (count <= 1) {
    return 'md:grid-cols-1 md:max-w-sm';
  }
  if (count === 2) {
    return 'md:grid-cols-2';
  }
  return 'md:grid-cols-3';
};

/** From a course card, whether this option opens that course; from the banner, nothing. */
const CourseAnswer = observer(function CourseAnswer({
  offer,
  entry,
}: {
  offer: LicenseOffer;
  entry: Entry;
}) {
  switch (entry.kind) {
    case 'collection': {
      return emptyFragment();
    }
    case 'course': {
      return offer.coveredProgramFamilyIds.includes(entry.programFamilyId) ? (
        <Typography as="p" variant="bodySmall" fontWeight="semibold" color="green">
          ✓ <T kind="Includes {{course}}" course={entry.courseTitle} />
        </Typography>
      ) : (
        <Typography as="p" variant="bodySmall" color="gray">
          ✕ <T kind="Does not include {{course}}" course={entry.courseTitle} />
        </Typography>
      );
    }
  }
});

/** What the option opens here, and, from a course card, whether it opens that course. */
const Coverage = observer(function Coverage({
  offer,
  entry,
}: {
  offer: LicenseOffer;
  entry: Entry;
}) {
  return (
    <div className="mt-auto border-t border-gray-200 pt-3">
      <CourseAnswer offer={offer} entry={entry} />
      <Typography as="p" variant="bodySmall" color="gray" className="mt-0.5">
        <T
          kind="Covers {{count}} of {{total}} courses in this collection"
          count={offer.coveredProgramFamilyIds.length}
          total={licensePurchaseStore.courseCount}
        />
      </Typography>
    </div>
  );
});

interface OptionProps {
  offer: LicenseOffer;
  entry: Entry;
  selected: boolean;
}

const Option = observer(function Option({ offer, entry, selected }: OptionProps) {
  const pick = () => {
    licensePurchaseStore.select(offer.productLicenseTypeId);
  };
  return (
    // The highlighted card is the selection. A visually hidden native radio, as in
    // Rvstyleguide's segmented control, keeps keyboard and screen-reader selection.
    <label
      data-intercom-target="license-picker-option"
      className={`relative flex min-w-0 cursor-pointer flex-col rounded-md p-5 transition-all focus-within:ring-[3px] focus-within:ring-primary/20 ${
        selected
          ? 'border-2 border-primary bg-primary/5'
          : 'border border-[#D6D6D6] bg-white hover:border-primary hover:shadow-lg'
      }`}
    >
      <input
        type="radio"
        name="license-type"
        value={String(offer.productLicenseTypeId)}
        checked={selected}
        onChange={pick}
        className="sr-only"
      />
      {/* A tab on the card's top edge: it marks the option without taking a line
          inside the card, so every name stays on one line and the cards line up. */}
      {offer.badgeLabel
        .map((label) => (
          <span
            key={label}
            className="absolute -top-2.5 right-3 rounded-full bg-[#70406d] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white"
          >
            <NotTranslated text={label} />
          </span>
        ))
        .getOrElse(emptyFragment)}
      <span className="flex min-w-0 items-center gap-2">
        {offer.iconUrl
          .map((src) => (
            <img key={src} src={src} alt="" className="h-6 w-6 flex-none object-contain" />
          ))
          .getOrElse(emptyFragment)}
        <Typography as="p" variant="subtitleSmall" fontWeight="semibold">
          <NotTranslated text={offer.name} />
        </Typography>
      </span>
      <Typography as="p" variant="h5" fontWeight="bold" className="mt-3">
        <ShelfPrice cents={offer.priceInCents} />
      </Typography>
      {/* One payment, not a subscription: the term says how long it lasts. */}
      <Typography as="p" variant="bodySmall" color="gray" className="mt-1">
        <TermLabel offer={offer} />
      </Typography>

      {offer.whoIsItFor
        .map((text) => (
          <Typography key={text} as="p" variant="bodySmall" className="mt-4 text-gray-700">
            <NotTranslated text={text} />
          </Typography>
        ))
        .getOrElse(emptyFragment)}

      {offer.includesFollowingContentHeader
        .andThen((header) => (header === offer.name ? nothing<string>() : just(header)))
        .map((header) => (
          <Typography
            key={header}
            as="p"
            variant="bodySmall"
            fontWeight="semibold"
            className="mt-4"
          >
            <NotTranslated text={header} />
          </Typography>
        ))
        .getOrElse(emptyFragment)}

      {offer.highlights.length > 0 && (
        <ul className="mb-4 mt-2 flex flex-col gap-1.5">
          {offer.highlights.map((line) => (
            <li key={line} className="relative pl-[18px] text-xs leading-snug text-gray-700">
              <span
                aria-hidden="true"
                className="absolute left-0.5 top-[0.4em] h-1 w-2 -rotate-45 border-b-2 border-l-2 border-teal-600"
              />
              <NotTranslated text={line} />
            </li>
          ))}
        </ul>
      )}

      <Coverage offer={offer} entry={entry} />
    </label>
  );
});

interface Props {
  flow: Choosing;
}

function ChooseLicense({ flow }: Props) {
  const offers = licensePurchaseStore.offersFor(flow.entry);

  return (
    <Pane
      intercomTarget="license-picker"
      head={
        <>
          <WizardSteps active={1} />
          <Typography
            id="license-picker-heading"
            as="h2"
            variant="subtitleLarge"
            fontWeight="bold"
            className="tracking-normal"
          >
            <T kind="Choose your license" />
          </Typography>
        </>
      }
      foot={
        <>
          <Button
            data-intercom-target="license-picker-cancel-button"
            color="gray"
            variant="outlined"
            size="md"
            onClick={licensePurchaseStore.close}
          >
            <T kind="Cancel" />
          </Button>
          <Button
            color="primary"
            size="md"
            disabled={licensePurchaseStore.selectedOffer.isNothing()}
            onClick={licensePurchaseStore.continueToPayment}
            data-intercom-target="license-picker-continue-button"
          >
            <T kind="Continue to payment" />
          </Button>
        </>
      }
    >
      <div
        role="radiogroup"
        data-intercom-target="license-picker-options"
        aria-labelledby="license-picker-heading"
        className={`box-border grid min-h-full grid-cols-1 gap-4 pt-6 ${columnsFor(offers.length)}`}
      >
        {offers.map((offer) => (
          <Option
            key={offer.productLicenseTypeId}
            offer={offer}
            entry={flow.entry}
            selected={flow.selectedId
              .map((id) => id === offer.productLicenseTypeId)
              .getOrElseValue(false)}
          />
        ))}
      </div>
    </Pane>
  );
}

export default observer(ChooseLicense);
